class StarterpackByo extends HTMLElement {
  constructor() {
    super();
    this.bundleVariantId = this.getAttribute('data-bundle-variant-id');
    this.minItems = parseInt(this.getAttribute('data-min'), 10) || 3;
    this.maxItems = parseInt(this.getAttribute('data-max'), 10) || 3;
    this.bundleSlug = this.getAttribute('data-bundle-slug') || 'starterpack-3x7-byo';
    this.selections = new Map();
    this.tiles = [];
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.tiles = Array.from(this.querySelectorAll('[data-tile]'));
    this.ctas = Array.from(this.querySelectorAll('[data-cta]'));
    this.cta = this.ctas[0]; // primary CTA — used as activeElement for cart-drawer focus
    this.progressLabels = Array.from(this.querySelectorAll('[data-progress-label]'));
    this.progressSegments = Array.from(this.querySelectorAll('[data-segment]'));
    this.errorEl = this.querySelector('[data-error]');

    this.tiles.forEach((tile) => {
      this.selections.set(tile.dataset.variantId, 0);

      // Tile (button) click toggles selection — ignore clicks from inner stepper buttons
      tile.addEventListener('click', (e) => {
        if (e.target.closest('[data-inc],[data-dec]')) return;
        const qty = this.getQty(tile);
        this.setQty(tile, qty > 0 ? 0 : 1);
      });

      tile.querySelector('[data-inc]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setQty(tile, this.getQty(tile) + 1);
      });
      tile.querySelector('[data-dec]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setQty(tile, this.getQty(tile) - 1);
      });
    });

    this.ctas.forEach((cta) => cta.addEventListener('click', (e) => this.onSubmit(e)));
    this.render();
  }

  getQty(tile) {
    return this.selections.get(tile.dataset.variantId) || 0;
  }

  totalSelected() {
    let sum = 0;
    this.selections.forEach((v) => { sum += v; });
    return sum;
  }

  setQty(tile, next) {
    const id = tile.dataset.variantId;
    const current = this.selections.get(id) || 0;
    const clamped = Math.max(0, next);
    if (clamped === current) return;

    const totalAfter = this.totalSelected() - current + clamped;
    if (totalAfter > this.maxItems) return;

    this.selections.set(id, clamped);
    this.render();
  }

  render() {
    const total = this.totalSelected();
    const atMax = total >= this.maxItems;

    this.tiles.forEach((tile) => {
      const qty = this.getQty(tile);
      tile.dataset.quantity = String(qty);
      if (qty > 0) tile.setAttribute('data-selected', '');
      else tile.removeAttribute('data-selected');

      const countEl = tile.querySelector('[data-count]');
      if (countEl) countEl.textContent = String(qty);

      const checkEl = tile.querySelector('[data-check]');
      if (checkEl) checkEl.dataset.qty = String(qty);

      const incBtn = tile.querySelector('[data-inc]');
      if (incBtn) incBtn.disabled = atMax;
    });

    // Segmented progress bar — fill segments left-to-right
    if (this.progressSegments.length > 0) {
      // Each progress track has minItems segments; we want to fill index < total
      // within each track group (mobile + desktop track may both exist).
      // Group segments by their parent track and fill per-group.
      const groups = new Map();
      this.progressSegments.forEach((seg) => {
        const track = seg.parentElement;
        if (!groups.has(track)) groups.set(track, []);
        groups.get(track).push(seg);
      });
      groups.forEach((segs) => {
        segs.forEach((seg, idx) => {
          if (idx < total) seg.setAttribute('data-filled', '');
          else seg.removeAttribute('data-filled');
        });
      });
    }

    // Mark host complete when bundle is fully selected (CSS hooks for green state)
    if (total >= this.minItems) this.setAttribute('data-complete', '');
    else this.removeAttribute('data-complete');

    this.progressLabels.forEach((label) => {
      const tpl = label.dataset.template || '<strong>{count}</strong> of {total} selected';
      label.innerHTML = tpl
        .replace('{count}', String(total))
        .replace('{total}', String(this.minItems));
    });

    const disabled = total !== this.minItems || this.isSubmitting;
    this.ctas.forEach((cta) => { cta.disabled = disabled; });
  }

  buildItems() {
    // Each selected tile becomes one real cart line item with the chosen 7er variant.
    // The `_bundle` property tags lines so the discount function can group them and
    // apply the bundle savings; `Pack` is shown to the customer as a per-line label.
    const items = [];
    this.tiles.forEach((tile) => {
      const qty = this.getQty(tile);
      if (qty <= 0) return;
      const title = tile.dataset.componentTitle || tile.querySelector('[data-tile-title]')?.textContent.trim() || '';
      items.push({
        id: tile.dataset.variantId,
        quantity: qty,
        properties: {
          _bundle: this.bundleSlug,
          Pack: title,
        },
      });
    });
    return items;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.setAttribute('data-visible', '');
  }

  clearError() {
    this.errorEl?.removeAttribute('data-visible');
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;
    if (this.totalSelected() !== this.minItems) return;

    this.clearError();
    this.isSubmitting = true;
    this.render();
    this.ctas.forEach((cta) => cta.setAttribute('aria-busy', 'true'));

    // Collect cart-drawer / cart-icon sections to re-render server-side,
    // matching the pattern at theme.js:6977 (product-bundle).
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    }));

    const body = JSON.stringify({
      items: this.buildItems(),
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    });

    try {
      const cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
      const fetchConfig = (window.theme && window.theme.utils && window.theme.utils.fetchConfig)
        ? window.theme.utils.fetchConfig('javascript')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' } };

      let parsedState;
      try {
        const response = await fetch(cartAddUrl, { ...fetchConfig, body });
        parsedState = await response.json();
      } catch (err) {
        console.error('[starterpack-byo] cart/add request failed', err);
        this.showError('Verbindungsfehler. Bitte erneut versuchen.');
        return;
      }

      if (parsedState && parsedState.status) {
        this.showError(parsedState.description || parsedState.message || 'Konnte nicht hinzugefügt werden.');
        document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } }));
        return;
      }

      // Add succeeded — best-effort post-actions, individually wrapped so a single failure
      // doesn't surface as a generic error after the line was already added.
      let cartJson = null;
      try {
        const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart.js';
        const cartFetchConfig = (window.theme && window.theme.utils && window.theme.utils.fetchConfig)
          ? window.theme.utils.fetchConfig('json', 'GET')
          : { method: 'GET', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } };
        cartJson = await (await fetch(cartUrl, cartFetchConfig)).json();
        // Hand off the freshly server-rendered sections from the add response
        // to the cart-drawer subscriber, so it can patch its DOM with up-to-date HTML.
        if (cartJson && parsedState && parsedState.sections) {
          cartJson.sections = parsedState.sections;
        }
      } catch (err) {
        console.warn('[starterpack-byo] cart fetch failed', err);
      }

      try {
        if (window.theme && window.theme.pubsub && cartJson) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'starterpack-byo', cart: cartJson });
        }
      } catch (err) {
        console.warn('[starterpack-byo] pubsub publish failed', err);
      }

      try {
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
      } catch (err) {
        console.warn('[starterpack-byo] ajaxProduct:added dispatch failed', err);
      }

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show(this.cta);
        }
      } catch (err) {
        console.warn('[starterpack-byo] cartDrawer.show failed', err);
      }

      this.resetSelections();
    } catch (err) {
      console.error('[starterpack-byo] unexpected error', err);
      this.showError('Etwas ist schiefgelaufen. Bitte erneut versuchen.');
    } finally {
      this.isSubmitting = false;
      this.ctas.forEach((cta) => cta.removeAttribute('aria-busy'));
      this.render();
    }
  }

  resetSelections() {
    this.selections.forEach((_, key) => this.selections.set(key, 0));
    this.render();
  }
}

if (!customElements.get('starterpack-byo')) {
  customElements.define('starterpack-byo', StarterpackByo);
}
